import clsx from 'clsx';
import { forwardRef } from 'react';
import { LucideIcon, Loader2 } from 'lucide-react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant */
  variant?: ButtonVariant;
  /** Size variant */
  size?: ButtonSize;
  /** Loading state - shows spinner and disables button */
  loading?: boolean;
  /** Icon on the left side */
  leftIcon?: LucideIcon;
  /** Icon on the right side */
  rightIcon?: LucideIcon;
  /** Button content */
  children: React.ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-primary-600 hover:bg-primary-700 text-white border-transparent',
  secondary: 'bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-200',
  ghost: 'bg-transparent hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-600 dark:text-neutral-400 border-transparent',
  destructive: 'bg-transparent hover:bg-error-50 dark:hover:bg-error-900/30 text-error-600 dark:text-error-400 border-transparent',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
};

const iconSizeClasses: Record<ButtonSize, string> = {
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

/**
 * Button - Standardized button component
 * 
 * Variants:
 * - primary: Main action (one per view)
 * - secondary: Secondary actions
 * - ghost: Subtle actions, cancel buttons
 * - destructive: Delete/remove actions
 * 
 * @example
 * <Button variant="primary" onClick={handleSubmit}>Save</Button>
 * <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
 * <Button variant="destructive" leftIcon={Trash2}>Delete</Button>
 * <Button loading>Saving...</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'md',
      loading = false,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      children,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    
    return (
      <button
        ref={ref}
        className={clsx(
          'inline-flex items-center justify-center font-medium rounded-md transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <Loader2 className={clsx('animate-spin', iconSizeClasses[size])} />
        ) : LeftIcon ? (
          <LeftIcon className={iconSizeClasses[size]} />
        ) : null}
        {children}
        {RightIcon && !loading && (
          <RightIcon className={iconSizeClasses[size]} />
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
